export default class Phone {
  static getCountryByCode (code) {
    return this.COUNTRIES.find (country => country.code === code)
  }

  static getDefaultCountry () {
    return this.getCountryByCode ('BR')
  }

  static getCountryOptions = () => {
    return this.COUNTRIES.map (country => ({
      value: country.code,
      label: country.dialCode,
      country
    }))
  }

  static toE164 (phoneValue) {
    if (!phoneValue) return ''
    
    if (typeof phoneValue === 'object' && phoneValue !== null) {
      const { countryCode, number } = phoneValue
      const country = this.getCountryByCode (countryCode)
      
      if (!country) return ''
      if (!number) return country.dialCode
      
      const cleanNumber = number.replace (/[()\s-]/g, '')
      return `${country.dialCode}${cleanNumber}`
    }
    
    if (typeof phoneValue === 'string' && phoneValue) {
      const cleanNumber = phoneValue.replace (/[()\s-]/g, '')
      return `+55${cleanNumber}`
    }
    
    return ''
  }

  static fromE164 (e164String) {
    if (!e164String) return { countryCode: 'BR', number: '' }
    
    const country = this.COUNTRIES.find (c => e164String.startsWith (c.dialCode))
    
    if (country) {
      const number = e164String.slice (country.dialCode.length)

      return {
        countryCode: country.code,
        number
      }
    }
    
    return {
      countryCode: 'BR',
      number: e164String.replace ('+55', '')
    }
  }

  static formatPhone (phoneValue, useDDI = false) {
    if (!phoneValue) return ''
    
    let e164 = phoneValue
    
    if (typeof phoneValue === 'object') {
      e164 = this.toE164 (phoneValue)
    }
    
    if (!e164.startsWith ('+')) {
      e164 = `+55${e164}`
    }
    
    const { countryCode, number } = this.fromE164 (e164)
    const country = this.getCountryByCode (countryCode)
    
    return `${useDDI ? country.dialCode : ''} ${this.applyMask (number, country.maskConfig)}`
  }

  static applyMask (value, maskConfig) {
    if (!value || !maskConfig) return ''
    
    const { blocks, delimiters, numericOnly } = maskConfig
    let cleanValue = numericOnly ? value.replace (/\D/g, '') : value

    if (!cleanValue) return ''
    
    let result = ''
    let valueIndex = 0
    let blockIndex = 0
    
    for (let i = 0; i < blocks.length; i++) {
      const blockSize = blocks[i]
      
      // Pular blocos de tamanho 0 (usado para posicionar delimitadores no início)
      if (blockSize === 0) {
        if (delimiters[blockIndex] && valueIndex < cleanValue.length) {
          result += delimiters[blockIndex]
          blockIndex++
        }
        continue
      }
      
      // Extrair caracteres do bloco atual
      const blockValue = cleanValue.slice (valueIndex, valueIndex + blockSize)
      
      if (!blockValue) break
      
      // Adicionar o valor do bloco
      result += blockValue
      valueIndex += blockSize
      
      // Adicionar delimitador após o bloco (se houver mais caracteres e se existe delimitador)
      if (valueIndex < cleanValue.length && delimiters[blockIndex]) {
        result += delimiters[blockIndex]
      }
      
      blockIndex++
    }
    
    return result
  }

  static isValidPhoneNumber (phoneValue) {
    const country = this.COUNTRIES.find (c => phoneValue.startsWith (c.dialCode))

    if (!country) return false

    const { minDigits, maxDigits } = country.validation
    const numberPart = phoneValue.slice (country.dialCode.length)
    const digits = numberPart.replace (/\D/g, '')
    const digitCount = digits.length
    
    return digitCount >= minDigits && digitCount <= maxDigits
  }

  static COUNTRIES = [
    {
      code: 'BR',
      name: 'Brasil',
      flagUrl: 'https://flagcdn.com/w40/br.png',
      dialCode: '+55',
      maskConfig: {
        blocks: [0, 2, 5, 4],
        delimiters: ['(', ') ', '-'],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 11
      },
      placeholder: '(00) 00000-0000'
    },
    {
      code: 'US',
      name: 'Estados Unidos',
      flagUrl: 'https://flagcdn.com/w40/us.png',
      dialCode: '+1',
      maskConfig: {
        blocks: [0, 3, 3, 4],
        delimiters: ['(', ') ', '-'],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '(000) 000-0000'
    },
    {
      code: 'GB',
      name: 'Reino Unido',
      flagUrl: 'https://flagcdn.com/w40/gb.png',
      dialCode: '+44',
      maskConfig: {
        blocks: [0, 4, 3, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 11
      },
      placeholder: '0000 000 0000'
    },
    {
      code: 'ES',
      name: 'Espanha',
      flagUrl: 'https://flagcdn.com/w40/es.png',
      dialCode: '+34',
      maskConfig: {
        blocks: [0, 3, 3, 3],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '000 000 000'
    },
    {
      code: 'PT',
      name: 'Portugal',
      flagUrl: 'https://flagcdn.com/w40/pt.png',
      dialCode: '+351',
      maskConfig: {
        blocks: [0, 3, 3, 3],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '000 000 000'
    },
    {
      code: 'AR',
      name: 'Argentina',
      flagUrl: 'https://flagcdn.com/w40/ar.png',
      dialCode: '+54',
      maskConfig: {
        blocks: [0, 2, 4, 4],
        delimiters: [' ', ' ', '-'],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '00 0000-0000'
    },
    {
      code: 'MX',
      name: 'México',
      flagUrl: 'https://flagcdn.com/w40/mx.png',
      dialCode: '+52',
      maskConfig: {
        blocks: [0, 3, 3, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '000 000 0000'
    },
    {
      code: 'DE',
      name: 'Alemanha',
      flagUrl: 'https://flagcdn.com/w40/de.png',
      dialCode: '+49',
      maskConfig: {
        blocks: [0, 3, 4, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 11
      },
      placeholder: '000 0000 0000'
    },
    {
      code: 'FR',
      name: 'França',
      flagUrl: 'https://flagcdn.com/w40/fr.png',
      dialCode: '+33',
      maskConfig: {
        blocks: [0, 1, 2, 2, 2, 2],
        delimiters: [' ', ' ', ' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '0 00 00 00 00'
    },
    {
      code: 'IT',
      name: 'Itália',
      flagUrl: 'https://flagcdn.com/w40/it.png',
      dialCode: '+39',
      maskConfig: {
        blocks: [0, 3, 3, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 10
      },
      placeholder: '000 000 0000'
    },
    {
      code: 'JP',
      name: 'Japão',
      flagUrl: 'https://flagcdn.com/w40/jp.png',
      dialCode: '+81',
      maskConfig: {
        blocks: [0, 2, 4, 4],
        delimiters: [' ', '-', '-'],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '00-0000-0000'
    },
    {
      code: 'AU',
      name: 'Austrália',
      flagUrl: 'https://flagcdn.com/w40/au.png',
      dialCode: '+61',
      maskConfig: {
        blocks: [0, 3, 3, 3],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '000 000 000'
    },
    {
      code: 'CN',
      name: 'China',
      flagUrl: 'https://flagcdn.com/w40/cn.png',
      dialCode: '+86',
      maskConfig: {
        blocks: [0, 3, 4, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 11,
        maxDigits: 11
      },
      placeholder: '000 0000 0000'
    },
    {
      code: 'IN',
      name: 'Índia',
      flagUrl: 'https://flagcdn.com/w40/in.png',
      dialCode: '+91',
      maskConfig: {
        blocks: [0, 5, 5],
        delimiters: [' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '00000 00000'
    },
    {
      code: 'CO',
      name: 'Colômbia',
      flagUrl: 'https://flagcdn.com/w40/co.png',
      dialCode: '+57',
      maskConfig: {
        blocks: [0, 3, 3, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '000 000 0000'
    },
    {
      code: 'CL',
      name: 'Chile',
      flagUrl: 'https://flagcdn.com/w40/cl.png',
      dialCode: '+56',
      maskConfig: {
        blocks: [0, 1, 4, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '0 0000 0000'
    },
    {
      code: 'PE',
      name: 'Peru',
      flagUrl: 'https://flagcdn.com/w40/pe.png',
      dialCode: '+51',
      maskConfig: {
        blocks: [0, 3, 3, 3],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '000 000 000'
    },
    {
      code: 'VE',
      name: 'Venezuela',
      flagUrl: 'https://flagcdn.com/w40/ve.png',
      dialCode: '+58',
      maskConfig: {
        blocks: [0, 3, 3, 4],
        delimiters: [' ', '-', '-'],
        numericOnly: true
      },
      validation: {
        minDigits: 10,
        maxDigits: 10
      },
      placeholder: '000-000-0000'
    },
    {
      code: 'UY',
      name: 'Uruguai',
      flagUrl: 'https://flagcdn.com/w40/uy.png',
      dialCode: '+598',
      maskConfig: {
        blocks: [0, 1, 3, 4],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 8,
        maxDigits: 8
      },
      placeholder: '0 000 0000'
    },
    {
      code: 'PY',
      name: 'Paraguai',
      flagUrl: 'https://flagcdn.com/w40/py.png',
      dialCode: '+595',
      maskConfig: {
        blocks: [0, 3, 3, 3],
        delimiters: [' ', ' ', ' '],
        numericOnly: true
      },
      validation: {
        minDigits: 9,
        maxDigits: 9
      },
      placeholder: '000 000 000'
    }
  ]
}
